import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/field';
import { ApiError } from '@/lib/api';
import { useMarkDone } from '@/api/queries';
import { reasonCopy } from './copy/reasons';

/**
 * Done, and the confirmation that has to explain it.
 *
 * Pressing Done is a one-way door for the client: the chat locks, the project
 * freezes, and the work goes to a reviewer. A non-technical person will not
 * infer any of that from the word "Done", so the dialog says it in the order
 * they need it - what happens to their work, what happens to this box, and what
 * they will see next.
 *
 * What it deliberately does not say: "merge", "staging", "branch", "review
 * queue". The client's model is "someone checks it, then it goes live".
 *
 * The server re-checks eligibility, so a stale tab cannot hand over an
 * undeployed change. When it refuses, the refusal is rendered in the same
 * vocabulary as the composer's greyed state rather than as an error.
 */

export function DoneDialog({
  projectId,
  open,
  onClose,
  onDone,
}: {
  projectId: number;
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
}) {
  const [note, setNote] = useState('');
  const mutation = useMarkDone(projectId);

  useEffect(() => {
    if (open) {
      setNote('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const refusal = error?.httpStatus === 409 ? reasonCopy(error.reasonCode ?? undefined) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={mutation.isPending}
      title="Hand this over for review?"
      description={
        <>
          A reviewer will check the work on your preview before anything goes live. Nothing you have asked for is lost
          or changed by doing this.
        </>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Keep working
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate(note.trim() || undefined, {
                onSuccess: () => {
                  onDone?.();
                  onClose();
                },
              })
            }
          >
            Hand it over
          </Button>
        </>
      }
    >
      <ul className="space-y-2 text-[0.90625rem] text-graphite-500">
        <Point>This conversation locks, so you will not be able to send new requests for now.</Point>
        <Point>Your preview site stays exactly where it is — you can keep looking at it.</Point>
        <Point>
          If the reviewer needs something changed, this comes straight back to you with their note and the box reopens.
        </Point>
      </ul>

      <div className="mt-4">
        <label htmlFor="done-note" className="eyebrow mb-1.5 block">
          Anything the reviewer should know? <span className="normal-case tracking-normal text-graphite-300">optional</span>
        </label>
        <Textarea
          id="done-note"
          rows={3}
          value={note}
          maxLength={2000}
          disabled={mutation.isPending}
          onChange={(event) => setNote(event.target.value)}
          placeholder="For example: the wording on the checkout button matters more than the colour."
        />
      </div>

      {refusal ? (
        <div className="mt-4 rounded-md bg-bone px-5 py-3.5 text-[0.90625rem]">
          <p className="font-semibold text-graphite-900">{refusal.title}</p>
          <p className="mt-0.5 text-graphite-500">{refusal.body}</p>
        </div>
      ) : error ? (
        <p className="mt-3 text-sm text-alarm">{error.message}</p>
      ) : null}
    </Dialog>
  );
}

function Point({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2.5">
      <span aria-hidden className="mt-[0.55rem] h-px w-3 shrink-0 bg-rule-strong" />
      <span className="min-w-0">{children}</span>
    </li>
  );
}
